/**
 * ヘルプの一覧と本文。ドロワーから「別のタブで開く」と、ここへ来る。
 *
 * 見出しの横のドロワーが普段の読み方で、この画面は**並べて読むため**の
 * ものである。だから左にセクション別の目次を置き、右に本文を出す。
 *
 * 知らないスラッグでは「無い」と書く。先頭の文書へ黙って倒すと、
 * 開いた人は別の説明を読んだことに気づけない。
 *
 * 見出しの横に置くもの（このページ自身の説明の「?」など）は、使う側が
 * `titleExtra` で渡す。
 */

import type { ReactNode } from "react";
import { Link, useParams } from "react-router";

import { Card, EmptyState, PageHeader } from "../ui/primitives";
import { HelpBody } from "./HelpBody";
import { helpPath } from "./help-path";
import type { HelpDoc, HelpRegistry } from "./help-registry";

export function createHelpPage(registry: HelpRegistry) {
  function Toc({ current }: { current: HelpDoc | null }) {
    return (
      <nav aria-label="ヘルプの目次" className="vd-help-toc">
        {registry.sections.map((section) => (
          <div key={section.key}>
            <div className="vd-stat-label">{section.title}</div>
            <ul>
              {section.docs.map((doc) => (
                <li key={doc.slug}>
                  {doc.slug === current?.slug ? (
                    <strong aria-current="page">{doc.title}</strong>
                  ) : (
                    <Link to={helpPath(doc.slug)}>{doc.title}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    );
  }

  return function HelpPage({
    titleExtra,
    extra,
  }: {
    titleExtra?: ReactNode;
    extra?: ReactNode;
  }) {
    const params = useParams();
    const slug = params["*"] ?? "";
    const doc = registry.findHelpDoc(slug);
    const missing = slug !== "" && doc === null;

    return (
      <div className="vd-page">
        <PageHeader
          title="ヘルプ"
          description="画面ごとの読み方と、数字の意味をまとめています。見出しの横の「?」からも同じものが開きます。"
          titleExtra={titleExtra}
          extra={extra}
        />
        <div className="vd-help-layout">
          <Card title="目次">
            <Toc current={doc} />
          </Card>
          <Card title={doc?.title ?? "ヘルプ"}>
            {missing ? (
              <EmptyState
                title="その説明はありません"
                description="リンクが古いか、まだ書かれていません。左の目次から探してください。"
              />
            ) : doc === null ? (
              <p style={{ margin: 0 }}>
                左の目次から、読みたい画面の説明を開いてください。
              </p>
            ) : (
              <HelpBody body={doc.body} />
            )}
          </Card>
        </div>
      </div>
    );
  };
}
